import React, { useState, useEffect } from 'react';
import '../css/Project.css';
import '../css/Collab.css';
import strykCover from '../assets/stryk-cover.webp';
import buxCover from '../assets/bux-cover.webp';
import wonderkindCover from '../assets/wonderkind-cover.webp';

import ButtonLarge from '../components/Button-large';
import '../css/Buttons.css';
import Project from '../components/Projects';
import ProjectStryk from './ProjectStryk';
import ProjectBuxZero from './ProjectBuxZero';
import ProjectWonderkind from './ProjectWonderkind';

import { Shape1, ShapeRectangle, Shape3 } from '../components/Shapes';
import {
  MdArrowForward,
  MdBuild,
  MdNavigation,
  MdPersonAddDisabled,
  MdChat,
  MdLocalCafe,
  MdViewQuilt,
  MdImageSearch,
  MdFlashOn,
  MdBakeryDining,
  MdTextFields,
  MdHandshake,
} from 'react-icons/md';
import collabLogo from '../assets/foot-note-logo.png';

import bananaBread from '../assets/banana-bread.webp';
import chocolateChipCookie from '../assets/chocolate-chip-cookie.webp';
import basqueCheesecake from '../assets/basque-cheesecake.webp';

import veganIcon from '../assets/vegan-icon.svg';
import glutenFreeIcon from '../assets/gluten-free-icon.svg';

// If the image is not appearing, check that '../assets/banana-bread.png' exists and is spelled correctly.
// If using create-react-app, only .png, .jpg, .jpeg, .gif, .webp, .svg are supported in imports.

const Collab = ({}) => {
  useEffect(() => {
    const elements = document.querySelectorAll('.move-right, .move-left');
    if (!elements.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('in-view');
            observer.unobserve(entry.target); // animate once
          }
        });
      },
      { root: null, rootMargin: '0px', threshold: 0.2 }
    );

    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const [isProjectVisible, setIsProjectVisible] = useState(false);

  const handleButtonClick = () => {
    setIsProjectVisible(!isProjectVisible); // Toggle visibility
  };

  return (
    <div className="homepage">
      {/* Hero Section */}
      <header className="intro">
        <div className="shapes-container-inside blur-desktop blur-mobile">
          <ShapeRectangle className="shape shape-5" />
          <Shape3 className="shape shape-6" />
        </div>

        <div className="hero">
          <div className="collab-logo">
            <img src={collabLogo} alt="Chunks" />
          </div>
          <h1>Collaboration proposal</h1>
        </div>
      </header>

      <div className="shapes-container-outside">
        <Shape1 className="shape shape-1" />
        <ShapeRectangle className="shape shape-2" />
        <Shape3 className="shape shape-3" />
        <Shape1 className="shape shape-4" />
      </div>

      {/* End of Hero Section */}

      <section className="section-intro">
        <div className="challenges-content">
          <p>
            Chunks is an Amsterdam-based baking brand creating fresh, handcrafted cookies and baked goods made
            with care and high-quality ingredients. We’re excited about a collaboration with <b>Foot Note</b> because
            our values around quality, authenticity, and enhancing the customer experience align well.
          </p>
        </div>
      </section>

      {/* Shared goals section */}
      <section className="solutions">
        <div className="solutions-grid">
          <div className="section-header">
            <h2>Shared goals</h2>
          </div>

          <div className="solutions-row-left move-right blue-background">
            <div className="solutions-icon">
              <MdLocalCafe size={48} />
            </div>
            <div className="solutions-content">
              <h3>Enriched menu</h3>
              <p>
                Enrich Foot Note’s menu with unique, artisanal baked goods that pair perfectly with their coffee.
              </p>
            </div>
          </div>

          <div className="solutions-row-right move-left blue-background">
            <div className="solutions-icon">
              <MdBakeryDining size={48} />
            </div>
            <div className="solutions-content">
              <h3>Increased production</h3>
              <p>Provide Chunks with a professional kitchen base for production.</p>
            </div>
          </div>

          <div className="solutions-row-left move-right blue-background">
            <div className="solutions-icon">
              <MdHandshake size={48} />
            </div>
            <div className="solutions-content">
              <h3>Strengthened brand perception</h3>
              <p>
                Improve both brands through collaboration, cross-promotion, and shared visibility.
              </p>
            </div>
          </div>

          <div className="solutions-description">
            <p>
              A partnership with Chunks would let Foot Note broaden its offering to create a holistic café experience:
              customers come not just for excellent coffee, but for a complementary handcrafted treat. This can elevate
              your café as a destination for both beverage and food experiences: increasing dwell time, customer
              satisfaction, and average spend.
            </p>
          </div>
        </div>
      </section>

      {/* Collaboration options Section */}
      <section className="case-studies">
        <div className="case-studies-grid">
          <div className="section-header">
            <h2>Collaboration ideas</h2>
          </div>

          <div className="cases">
            <div className="collab-model move-right">
              <h2>Wholesale Supply + Kitchen Share</h2>

              <ul>
                <li>Chunks uses Foot Note’s kitchen during open and closed times for production.</li>
                <li>
                  Chunks supplies bakes to Foot Note at a <b>below market</b> wholesale rate, with consistent pricing.
                </li>
                <li>Foot Note sells them on site at retail price, retaining full markup.</li>
              </ul>

              <div className="collab-advantages">
                <h3>Advantages for Foot Note:</h3>
                <p>
                  Low risk, simple cost structure, reliable supply of high-quality baked goods aligned with brand.
                </p>
              </div>

              <div className="collab-advantages">
                <h3>Advantages for Chunks:</h3>
                <p>
                  Access to a professional kitchen, embedded presence in a café that attracts quality-conscious
                  customers.
                </p>
              </div>
            </div>

            <div className="collab-model move-left">
              <h2>Consignment + Kitchen Share</h2>

              <ul>
                <li>Chunks uses Foot Note’s kitchen during open and closed times for production.</li>
                <li>Chunks supplies bakes to Foot Note on a consignment basis.</li>
                <li>
                  Foot Note sells them in-store; revenue is split (for example, 70% Chunks / 30% Foot Note).
                </li>
              </ul>

              <div className="collab-advantages">
                <h3>Advantages for Foot Note:</h3>
                <p>No upfront inventory cost, shared incentive to promote bakes, and enriched offering for customers.</p>
              </div>

              <div className="collab-advantages">
                <h3>Advantages for Chunks:</h3>
                <p>
                  Access to a professional kitchen, embedded presence in a café that attracts quality-conscious
                  customers.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Product offering Section */}
      <section className="case-studies">
        <div className="case-studies-grid">
          <div className="section-header">
            <h2>Product offering</h2>
          </div>

          <div className="cases">
            <div className="product-card">
              <div className="product-image">
                <img src={bananaBread} alt="Banana Bread" />
              </div>

              <div className="product-info">
                <div className="product-title">
                  <h3>Banana Bread</h3>
                  <img src={veganIcon} alt="Vegan" className="vegan-icon" />
                  <img src={glutenFreeIcon} alt="Gluten Free" className="vegan-icon" />
                </div>

                <span className="product-price">Retail: <strong>€3.50</strong> per slice</span>
                <span className="product-price">Wholesale: <strong>€2.65</strong>  per slice</span>
                <p>
                  Moist, flavorful banana bread made with ripe bananas, candied walnuts and dark chocolate.
                </p>
                <span className="product-extra-info">Gluten free option available</span>
              </div>
            </div>

            <div className="product-card">
              <div className="product-image">
                <img src={chocolateChipCookie} alt="Chocolate chip cookie" />
              </div>

              <div className="product-info">
                <div className="product-title">
                  <h3>Chocolate chip cookies</h3>
                  <img src={veganIcon} alt="Vegan" className="vegan-icon" />
                  <img src={glutenFreeIcon} alt="Gluten Free" className="vegan-icon" />
                </div>

                <span className="product-price">Retail: <strong>€2.80</strong> per cookie</span>
                <span className="product-price">Wholesale: <strong>€1.50</strong>  per cookie</span>

                <p>A perfect mix between crunchy and chewy, with a deep dark chocolate flavour.</p>
                <span className="product-extra-info">Vegan and gluten free options available</span>
              </div>
            </div>

            <div className="product-card">
              <div className="product-image">
                <img src={basqueCheesecake} alt="Basque cheesecake" />
              </div>

              <div className="product-info">
                <div className="product-title">
                  <h3>Basque burnt cheesecake</h3>
                  <img src={glutenFreeIcon} alt="Gluten Free" className="vegan-icon" />
                </div>

                <span className="product-price">Retail: <strong>€6.00</strong> per slice</span>
                <span className="product-price">Wholesale: <strong>€3.00</strong>  per slice</span>
                <p>
                  Soft, creamy cheesecake with a caramelized top and a rich, velvety texture.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="outro">
        <div className="hero-outro">
          <h1>Thanks!</h1>
        </div>
      </section>
    </div>
  );
};

export default Collab;
